import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FiShield,
  FiCheckCircle,
  FiCreditCard,
  FiLock,
  FiFileText,
  FiDollarSign,
  FiArrowRight,
  FiZap,
  FiMapPin,
  FiChevronDown,
  FiSearch,
  FiPhone,
  FiMail,
  FiHome,
} from 'react-icons/fi';
import { MdFingerprint } from 'react-icons/md';
import { LuIndianRupee } from 'react-icons/lu';
import { loanBackendUrl, saveLendingPageUrl, taskApiDirectUrl, taskApiPostUrl, crossVerticalPanUrl,formatApiMessage } from '../config/taskApi';
import { createEnquiryAutosave } from '../utils/saveEnquiryDraft';
import './apply-now.css';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const AADHAAR_REGEX = /^\d{12}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PINCODE_REGEX = /^[1-9]\d{5}$/;
const ONLY_DIGITS = /^\d+$/;

const formatIndianCurrency = (value) => {
  const num = Number(String(value).replace(/,/g, ''));
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-IN').format(num);
};

const maskMobileNumber = (mobile) => {
  const digits = String(mobile).replace(/\D/g, '');
  if (digits.length !== 10) return mobile;
  return `${digits.slice(0, 2)}****${digits.slice(-4)}`;
};

const SUCCESS_STEPS = [
  {
    title: 'Application review',
    text: 'Our team verifies your details within a few working hours.',
  },
  {
    title: 'Call from our expert',
    text: 'We contact you on your registered mobile number with the next steps.',
  },
  {
    title: 'Quick disbursal',
    text: 'Once approved, funds are transferred directly to your bank account.',
  },
];

const MIN_LOAN_AMOUNT = 10000;
const MAX_LOAN_AMOUNT = 500000;
const MIN_MONTHLY_INCOME = 15000;
const MAX_MONTHLY_INCOME = 5000000;
const MAX_OBLIGATIONS = 5000000;
const MIN_ADDRESS_LENGTH = 15;
const MAX_ADDRESS_LENGTH = 500;
const MAX_EMAIL_LENGTH = 254;
const OTP_RESEND_SECONDS = 60;
const AADHAAR_OTP_SEND_SUCCESS = 'SOS174';
const AADHAAR_OTP_VERIFY_SUCCESS = 'SRC001';
const isLocalDevHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

const mobileSendOtpUrl = () =>
  isLocalDevHost() ? '/Api/TaskApi/sendOtp' : 'https://loansalary.in/lms/Api/TaskApi/sendOtp';

const mobileVerifyOtpUrl = () =>
  isLocalDevHost() ? '/Api/TaskApi/verifyOtp' : 'https://loansalary.in/lms/Api/TaskApi/verifyOtp';

const isMobileOtpApiSuccess = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (data.status === true || data.Status === 1 || data.success === true) return true;
  const code = String(data.responseCode || data.code || '').toUpperCase();
  return ['SRC001', 'SUCCESS', 'OK'].includes(code);
};

const extractMobileOtpMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === 'string') return data.trim() || fallback;
  return (
    data.message ||
    data.Message ||
    data.responseMessage ||
    data.error ||
    data.Error ||
    fallback
  );
};

const formatCountdown = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const getAadhaarApiFeedback = (data, fallbackMessage) => ({
  code: data?.responseCode || '',
  message: data?.responseMessage || data?.message || fallbackMessage,
});

const validatePan = (pan) => {
  const value = pan.trim();
  if (!value) return 'PAN is required';
  if (value.length < 10) return 'PAN must be 10 characters';
  if (!PAN_REGEX.test(value)) return 'Enter a valid PAN (e.g. ABCDE1234F)';
  return '';
};

const validateAadhaar = (aadhaar) => {
  const value = aadhaar.trim();
  if (!value) return 'Aadhaar is required';
  if (!AADHAAR_REGEX.test(value)) return 'Enter a valid 12-digit Aadhaar number';
  if (/^(\d)\1{11}$/.test(value)) return 'Enter a valid Aadhaar number';
  return '';
};

const validateOtp = (otp) => {
  if (!otp) return 'OTP is required';
  if (otp.length !== 6) return 'Enter 6-digit OTP';
  if (!ONLY_DIGITS.test(otp)) return 'OTP must contain digits only';
  return '';
};

const parseFullName = (fullName) => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', middleName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], middleName: '', lastName: '' };
  if (parts.length === 2) return { firstName: parts[0], middleName: '', lastName: parts[1] };
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

const mapGender = (gender) => {
  const value = String(gender || '').toLowerCase();
  if (value.startsWith('m')) return 'male';
  if (value.startsWith('f')) return 'female';
  return value;
};

const extractAadhaarLastFour = (...values) => {
  for (const value of values) {
    if (!value) continue;
    const digits = String(value).replace(/\D/g, '');
    if (digits.length >= 4) return digits.slice(-4);
  }
  return '';
};

const buildAadhaarAddress = (data) => {
  const parts = [
    data.careOf,
    data.house,
    data.street,
    data.locality,
    data.vtcName,
    data.subDistrict,
    data.district,
    data.postOfficeName,
  ]
    .map((part) => (part || '').trim())
    .filter(Boolean);
  return parts.join(', ');
};

const REAPPLY_ALLOWED_LOAN_STATUSES = ['FRESH', 'NEW', 'CLOSED', 'CLOSE', 'REJECT', 'REJECTED'];

const isLeadStatusAllowedToApply = (loanStatus) => {
  if (!loanStatus) return true;
  return REAPPLY_ALLOWED_LOAN_STATUSES.includes(String(loanStatus).toUpperCase());
};

const getLeadStatusDecision = (customerData) => {
  const loanStatus = customerData?.status
    ? String(customerData.status).toUpperCase()
    : '';

  if (!loanStatus) {
    return {
      allowed: true,
      feedback: { type: 'success', message: 'Customer details loaded successfully.' },
    };
  }

  if (isLeadStatusAllowedToApply(loanStatus)) {
    return {
      allowed: true,
      feedback: {
        type: 'success',
        message: `Latest Status: ${loanStatus} (You can re-apply)`,
      },
    };
  }

  return {
    allowed: false,
    feedback: {
      type: 'error',
      message: `Your loan status is ${loanStatus}. Cannot re-apply.`,
    },
  };
};

const PAN_NOT_FOUND_MESSAGE = 'PAN does not exist. Enter your correct PAN.';

const CROSS_VERTICAL_DISBURSED_BLOCK_MESSAGE =
  'You have already taken a loan from another NBFC. A new application cannot be submitted at this time.';

/** Count group verticals that already have at least one DISBURSED loan for this PAN */
const countDisbursedVerticals = (apiData) => {
  const rows = Array.isArray(apiData?.data) ? apiData.data : [];
  let count = 0;

  for (const vertical of rows) {
    const records = Array.isArray(vertical?.records) ? vertical.records : [];

    const hasDisbursed = records.some(
      (record) => String(record?.lead_status || '').toUpperCase() === 'DISBURSED'
    );

    if (hasDisbursed) count += 1;
  }

  return count;
};

/** 2+ verticals already DISBURSED → block 3rd apply */
const isCrossVerticalDisbursedBlocked = (apiData) =>
  countDisbursedVerticals(apiData) >= 2;

const getCrossVerticalApiMessage = (data, err, fallback) => {
  const fromData =
    data?.message ||
    data?.Message ||
    data?.error ||
    data?.responseMessage ||
    (typeof data === 'string' ? data : '');

  if (fromData) return formatApiMessage(String(fromData));

  const errData = err?.response?.data;

  if (typeof errData === 'string' && errData.trim()) {
    return formatApiMessage(errData);
  }

  if (errData && typeof errData === 'object') {
    const nested =
      errData.message ||
      errData.Message ||
      errData.error ||
      errData.responseMessage ||
      '';

    if (nested) return formatApiMessage(String(nested));
  }

  if (err?.message) return formatApiMessage(err.message);

  return fallback;
};


const isPanNotFoundResponse = (data) => {
  if (!data || data.failed) return false;

  const code = String(data.responseCode || '').toUpperCase();
  const message = String(data.responseMessage || data.message || '').toLowerCase();

  return code === 'ENI004' || message.includes('no information found');
};

const isVerifyPanSuccess = (verifyPanResult) => {
  if (isPanNotFoundResponse(verifyPanResult)) return false;

  if (!verifyPanResult || verifyPanResult.failed || verifyPanResult.status === false) {
    return false;
  }

  if (verifyPanResult.responseCode !== 'SRC001' || !verifyPanResult.data) {
    return false;
  }

  const panStatus = String(verifyPanResult.data.status || '').toUpperCase();
  if (panStatus && panStatus !== 'VALID') {
    return false;
  }

  return true;
};

const buildPanPayloadFields = (verifyPanResult) => {
  const emptyFields = {
    pan_responseCode: '',
    pan_responseMessage: '',
    pan_referenceId: '',
    pan_firstName: '',
    pan_lastName: '',
    pan_address: '',
    pan_gender: '',
    pan_aadhaarLinked: '',
    pan_dob: '',
    pan_fullName: '',
    pan_maskedAadhaarNumber: '',
    pan_category: '',
    pan_verifiedPan: '',
  };

  if (!verifyPanResult || verifyPanResult.failed) {
    return emptyFields;
  }

  const data = verifyPanResult.data || {};

  return {
    pan_responseCode: verifyPanResult.responseCode || '',
    pan_responseMessage: verifyPanResult.responseMessage || '',
    pan_referenceId: verifyPanResult.referenceId || '',
    pan_firstName: data.firstName || '',
    pan_lastName: data.lastName || '',
    pan_address: data.address || '',
    pan_gender: data.gender || '',
    pan_aadhaarLinked: data.aadhaarLinked || '',
    pan_dob: data.dob || data.dateOfBirth || '',
    pan_fullName: data.fullName || '',
    pan_maskedAadhaarNumber: data.maskedAadhaarNumber || '',
    pan_category: data.category || '',
    pan_verifiedPan: data.pan || '',
  };
};

const isLoanBlockedResponse = (data) => {
  const msg = String(data?.message || '').toLowerCase();
  return msg.includes('cannot re-apply') || msg.includes('loan status is');
};

const isCustomerNotFoundResponse = (data) => {
  if (!data || data.status === true || isLoanBlockedResponse(data)) return false;
  const msg = String(data.message || '').toLowerCase();
  return msg.includes('not found') || msg.includes('no customer');
};

const validateMobile = (mobile, { required = true, label = 'Mobile number' } = {}) => {
  const value = mobile.trim();
  if (!value) return required ? `${label} is required` : '';
  if (!MOBILE_REGEX.test(value)) return 'Enter a valid 10-digit mobile (starts with 6–9)';
  return '';
};

const validateEmail = (email, { required = true, label = 'Email' } = {}) => {
  const value = email.trim();
  if (!value) return required ? `${label} is required` : '';
  if (value.length > MAX_EMAIL_LENGTH) return `${label} is too long`;
  if (!EMAIL_REGEX.test(value)) return 'Enter a valid email address';
  return '';
};

const validateAmount = (
  value,
  { required = true, label, min, max, allowZero = false }
) => {
  const trimmed = String(value).trim();
  if (!trimmed) return required ? `${label} is required` : '';

  if (!ONLY_DIGITS.test(trimmed)) return `${label} must be a whole number`;
  const amount = Number(trimmed);
  if (!allowZero && amount <= 0) return `${label} must be greater than 0`;
  if (min !== undefined && amount < min) {
    return `${label} must be at least ₹${min.toLocaleString('en-IN')}`;
  }
  if (max !== undefined && amount > max) {
    return `${label} must not exceed ₹${max.toLocaleString('en-IN')}`;
  }
  return '';
};

const validatePincode = (pincode) => {
  const value = String(pincode).trim();
  if (!value) return 'Pincode is required';
  if (!PINCODE_REGEX.test(value)) return 'Enter a valid 6-digit pincode';
  return '';
};

const validateAddress = (address) => {
  const value = address.trim();
  if (!value) return 'Address is required';
  if (value.length < MIN_ADDRESS_LENGTH) {
    return `Address must be at least ${MIN_ADDRESS_LENGTH} characters`;
  }
  if (value.length > MAX_ADDRESS_LENGTH) {
    return `Address must not exceed ${MAX_ADDRESS_LENGTH} characters`;
  }
  if (!/[a-zA-Z0-9]/.test(value)) return 'Enter a valid address';
  return '';
};

const BENEFITS = [
  {
    title: 'Instant Approval',
    text: 'Real-time PAN & Aadhaar verification',
  },
  {
    title: '100% Paperless',
    text: 'No branch visit, no documents',
  },
  {
    title: 'Safe & Secure',
    text: 'Bank-grade 256-bit encryption',
  },
];

const initialHiddenProfile = {
  firstName: '',
  middleName: '',
  lastName: '',
  dob: '',
  gender: '',
  aadhaarLastFour: '',
};

const initialStep1 = {
  panNumber: '',
  aadhaarNumber: '',
  otp: '',
};

const initialStep2 = {
  monthlyIncome: '',
  loanAmount: '',
  mobile: '',
  altMobile: '',
  email: '',
  officialEmail: '',
  obligations: '',
  state: '',
  city: '',
  pincode: '',
  address: '',
  termsAccepted: false,
};

const APPLY_NOW_STORAGE_KEY = 'loanonsalary_apply_now_draft_v1';

const readApplyNowDraft = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(APPLY_NOW_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch (err) {
    console.warn('Failed to read apply-now draft:', err);
    return null;
  }
};

const clearApplyNowDraft = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(APPLY_NOW_STORAGE_KEY);
  } catch (err) {
    console.warn('Failed to clear apply-now draft:', err);
  }
};

const normalizeDraftStep = (draft) => {
  let nextStep = Number(draft?.step) || 1;
  if (nextStep < 1 || nextStep > 3) nextStep = 1;
  if (nextStep >= 2 && !draft?.mobileVerified) nextStep = 1;
  if (nextStep >= 3 && !draft?.otpVerified) nextStep = 2;
  return nextStep;
};

const getOtpResumeState = (sentAt, alreadySentFlag = false) => {
  const ts = Number(sentAt) || 0;
  if (!ts && !alreadySentFlag) {
    return { sent: false, remaining: 0 };
  }

  if (!ts && alreadySentFlag) {
    return { sent: true, remaining: 0 };
  }

  const elapsed = Math.floor((Date.now() - ts) / 1000);
  if (elapsed >= OTP_RESEND_SECONDS) {
    return { sent: true, remaining: 0 };
  }

  return {
    sent: true,
    remaining: Math.max(0, OTP_RESEND_SECONDS - elapsed),
  };
};

const mapCustomerToHiddenProfile = (data) => ({
  firstName: data.first_name || '',
  middleName: data.middle_name || '',
  lastName: data.sur_name || '',
  dob: data.dob || '',
  gender: data.gender ? String(data.gender).toLowerCase() : '',
  state: data.state_name || '',
  city: data.city_name || '',
  pincode: data.pincode || '',
  stateId: data.state_id ? String(data.state_id) : '',
  cityId: data.city_id ? String(data.city_id) : '',
});

const CustomDropdown = ({
  id,
  label,
  required,
  icon,
  value,
  options,
  placeholder,
  disabled,
  onChange,
  error,
  searchable = true,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.trim().toLowerCase())
  );

  const selectedOption = options.find((option) => option.value === value);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
    setSearch('');
  };

  return (
    <div
      ref={dropdownRef}
      className={`apply-now-field apply-now-dropdown-field ${error ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''}`}
    >
      <label htmlFor={id}>
        {label} {required && <span>*</span>}
      </label>

      <button
        type="button"
        id={id}
        className={`apply-now-dropdown-trigger ${open ? 'is-open' : ''} ${selectedOption ? 'has-value' : ''}`}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
      >
        <span className="apply-now-dropdown-trigger-icon">{icon}</span>
        <span className={`apply-now-dropdown-value ${selectedOption ? '' : 'is-placeholder'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="apply-now-dropdown-chevron">
          <FiChevronDown />
        </span>
      </button>

      {open && !disabled && (
        <div className="apply-now-dropdown-menu">
          {searchable && options.length > 6 && (
            <div className="apply-now-dropdown-search">
              <FiSearch />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                autoFocus
              />
            </div>
          )}

          <ul className="apply-now-dropdown-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    className={`apply-now-dropdown-option ${option.value === value ? 'is-selected' : ''}`}
                    onClick={() => handleSelect(option.value)}
                  >
                    <span>{option.label}</span>
                    {option.value === value && <FiCheckCircle />}
                  </button>
                </li>
              ))
            ) : (
              <li className="apply-now-dropdown-empty">No results found</li>
            )}
          </ul>
        </div>
      )}

      {error && <span className="apply-now-error">{error}</span>}
    </div>
  );
};

const ApplyNow = () => {
  const savedDraftRef = useRef(undefined);
  if (savedDraftRef.current === undefined) {
    savedDraftRef.current = readApplyNowDraft();
  }
  const savedDraft = savedDraftRef.current;
  const storageReadyRef = useRef(false);

  const mobileOtpResume = !savedDraft?.mobileVerified
    ? getOtpResumeState(savedDraft?.mobileOtpSentAt, savedDraft?.mobileOtpSent)
    : { sent: false, remaining: 0 };
  const aadhaarOtpResume =
    savedDraft?.mobileVerified && !savedDraft?.otpVerified
      ? getOtpResumeState(savedDraft?.aadhaarOtpSentAt, savedDraft?.otpSent)
      : { sent: false, remaining: 0 };

  const [step, setStep] = useState(() => normalizeDraftStep(savedDraft));
  const [step1, setStep1] = useState(() => ({
    ...initialStep1,
    ...(savedDraft?.step1 || {}),
    otp: '',
  }));
  const [step2, setStep2] = useState(() => ({
    ...initialStep2,
    ...(savedDraft?.step2 || {}),
  }));
  const [hiddenProfile, setHiddenProfile] = useState(() => ({
    ...initialHiddenProfile,
    ...(savedDraft?.hiddenProfile || {}),
  }));
  const [errors, setErrors] = useState({});
  const [mobileNumber, setMobileNumber] = useState(() => savedDraft?.mobileNumber || '');
  const [mobileOtp, setMobileOtp] = useState('');
  const [mobileOtpSent, setMobileOtpSent] = useState(() => mobileOtpResume.sent);
  const [mobileOtpSentAt, setMobileOtpSentAt] = useState(
    () => Number(savedDraft?.mobileOtpSentAt) || 0
  );
  const [mobileVerified, setMobileVerified] = useState(() => Boolean(savedDraft?.mobileVerified));
  const [mobileOtpMessage, setMobileOtpMessage] = useState(() =>
    mobileOtpResume.sent
      ? { type: 'success', message: 'OTP already sent. Enter OTP or wait to resend.' }
      : null
  );
  const [sendingMobileOtp, setSendingMobileOtp] = useState(false);
  const [verifyingMobileOtp, setVerifyingMobileOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(() => aadhaarOtpResume.sent);
  const [aadhaarOtpSentAt, setAadhaarOtpSentAt] = useState(
    () => Number(savedDraft?.aadhaarOtpSentAt) || 0
  );
  const [otpVerified, setOtpVerified] = useState(() => Boolean(savedDraft?.otpVerified));
  const [otpApiMessage, setOtpApiMessage] = useState(() =>
    aadhaarOtpResume.sent
      ? { type: 'success', message: 'OTP already sent. Enter OTP or wait to resend.' }
      : null
  );
  const [otpCountdown, setOtpCountdown] = useState(() => {
    const restoredStep = normalizeDraftStep(savedDraft);
    if (restoredStep === 1) return mobileOtpResume.remaining;
    if (restoredStep === 2) return aadhaarOtpResume.remaining;
    return 0;
  });
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateId, setStateId] = useState(() =>
    savedDraft?.stateId ? String(savedDraft.stateId) : ''
  );
  const [cityId, setCityId] = useState(() =>
    savedDraft?.cityId ? String(savedDraft.cityId) : ''
  );
  const [pincodeList, setPincodeList] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingPincodes, setLoadingPincodes] = useState(false);
  const citiesRequestRef = useRef(0);
  const pincodesRequestRef = useRef(0);
  const pendingPincodeRef = useRef('');
  const pendingAadhaarLocationRef = useRef(null);
  const lastVerifiedPanRef = useRef(
    savedDraft?.lastVerifiedPan || savedDraft?.step1?.panNumber || ''
  );
  const panCheckInFlightRef = useRef('');
  const submitInFlightRef = useRef(false);
  const enquiryAutosaveRef = useRef(null);
  const enquirySnapshotRef = useRef({});
  const submittedRef = useRef(false);
  const [panChecking, setPanChecking] = useState(false);
  const [panStatusFeedback, setPanStatusFeedback] = useState(
    () => savedDraft?.panStatusFeedback || null
  );
  const [panVerifyStatus, setPanVerifyStatus] = useState(
    () => (savedDraft?.panVerifyStatus === 1 ? 1 : 0)
  );
  const [panResponseData, setPanResponseData] = useState(
    () => savedDraft?.panResponseData || null
  );
  const [panVerified, setPanVerified] = useState(() => Boolean(savedDraft?.panVerified));
  const [panLeadBlocked, setPanLeadBlocked] = useState(() => Boolean(savedDraft?.panLeadBlocked));
  const [statesLoadError, setStatesLoadError] = useState('');

  useEffect(() => {
    storageReadyRef.current = true;
  }, []);

  useEffect(() => {
    if (!storageReadyRef.current || submitted) return;

    const draftPayload = {
      step,
      step1: {
        panNumber: step1.panNumber || '',
        aadhaarNumber: step1.aadhaarNumber || '',
      },
      step2: {
        ...step2,
        termsAccepted: Boolean(step2.termsAccepted),
      },
      hiddenProfile,
      mobileNumber,
      mobileVerified,
      mobileOtpSent: mobileVerified ? false : mobileOtpSent,
      mobileOtpSentAt: mobileVerified ? 0 : mobileOtpSentAt,
      otpVerified,
      otpSent: otpVerified ? false : otpSent,
      aadhaarOtpSentAt: otpVerified ? 0 : aadhaarOtpSentAt,
      stateId,
      cityId,
      panVerifyStatus,
      panVerified,
      panLeadBlocked,
      panStatusFeedback,
      panResponseData,
      lastVerifiedPan: lastVerifiedPanRef.current || step1.panNumber || '',
      updatedAt: Date.now(),
    };

    try {
      window.localStorage.setItem(APPLY_NOW_STORAGE_KEY, JSON.stringify(draftPayload));
    } catch (err) {
      console.warn('Failed to save apply-now draft:', err);
    }
  }, [
    step,
    step1.panNumber,
    step1.aadhaarNumber,
    step2,
    hiddenProfile,
    mobileNumber,
    mobileVerified,
    mobileOtpSent,
    mobileOtpSentAt,
    otpVerified,
    otpSent,
    aadhaarOtpSentAt,
    stateId,
    cityId,
    panVerifyStatus,
    panVerified,
    panLeadBlocked,
    panStatusFeedback,
    panResponseData,
    submitted,
  ]);

  useEffect(() => {
    axios
      .get(taskApiDirectUrl('getState'))
      .then((res) => {
        if (res.data.Status === 1) {
          setStates(res.data.Data);
          setStatesLoadError('');
        } else {
          setStatesLoadError(res.data.Message || 'Failed to fetch states');
        }
      })
      .catch((err) => {
        console.error('Failed fetching states:', err);
        setStatesLoadError('Could not load states. Please refresh the page.');
      });
  }, []);

  useEffect(() => {
    const countdownActive =
      (step === 1 && mobileOtpSent) || (step === 2 && otpSent);
    if (!countdownActive || otpCountdown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setOtpCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [step, mobileOtpSent, otpSent, otpCountdown]);

  // Keep latest CUMULATIVE form snapshot for LMS enquiry autosave (never includes OTP).
  const enquiryStepName =
    step === 1 ? 'mobile' : step === 2 ? 'identity' : 'loan_details';
  enquirySnapshotRef.current = {
    mobile: mobileNumber || step2.mobile,
    Mobile: mobileNumber || step2.mobile,
    mobile_verified: mobileVerified ? 1 : 0,
    first_name: hiddenProfile.firstName,
    middle_name: hiddenProfile.middleName,
    last_name: hiddenProfile.lastName,
    Name: [hiddenProfile.firstName, hiddenProfile.middleName, hiddenProfile.lastName]
      .filter(Boolean)
      .join(' '),
    email: step2.email,
    email_personal: step2.email,
    loan_amount: step2.loanAmount,
    city: step2.city,
    city_name: step2.city,
    city_id: cityId,
    state_id: stateId,
    pincode: step2.pincode,
    pin: step2.pincode,
    pan: step1.panNumber,
    pancard: step1.panNumber,
    aadhaar: step1.aadhaarNumber,
    aadhaar_number: step1.aadhaarNumber,
    step: enquiryStepName,
    remarks: `apply-now step ${enquiryStepName}`,
  };
  submittedRef.current = submitted;

  useEffect(() => {
    const autosave = createEnquiryAutosave({
      getSnapshot: () =>
        submittedRef.current ? null : enquirySnapshotRef.current,
      debounceMs: 700,
    });
    enquiryAutosaveRef.current = autosave;

    const flushOnLeave = () => {
      if (submittedRef.current) return;
      autosave.flushOnLeave();
    };

    window.addEventListener('pagehide', flushOnLeave);
    window.addEventListener('beforeunload', flushOnLeave);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushOnLeave();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('pagehide', flushOnLeave);
      window.removeEventListener('beforeunload', flushOnLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      autosave.dispose();
      enquiryAutosaveRef.current = null;
    };
  }, []);

  // Debounced field progress → Enquires bucket (cumulative payload, before final submit only).
  useEffect(() => {
    if (submitted) return undefined;
    enquiryAutosaveRef.current?.scheduleSave();
    return undefined;
  }, [
    submitted,
    mobileNumber,
    mobileVerified,
    step2.mobile,
    step2.email,
    step2.loanAmount,
    step2.city,
    step2.pincode,
    cityId,
    stateId,
    step1.panNumber,
    step1.aadhaarNumber,
    panVerified,
    otpVerified,
    hiddenProfile.firstName,
    hiddenProfile.middleName,
    hiddenProfile.lastName,
  ]);

  // Immediate save on step / Next / verification progress (cumulative; not on final submit).
  useEffect(() => {
    if (submitted) return undefined;
    enquiryAutosaveRef.current?.saveNow({ force: true });
    return undefined;
  }, [submitted, step, mobileVerified, panVerified, otpVerified]);

  const setFieldError = (field, message) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const formatAadhaarDisplay = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 12);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const formatAadhaarRaw = (value) => value.replace(/\D/g, '').slice(0, 12);

  const handleStep1Change = (field, value) => {
    let nextValue = value;

    if (field === 'panNumber') {
      nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);

      if (nextValue !== lastVerifiedPanRef.current) {
        lastVerifiedPanRef.current = '';
        panCheckInFlightRef.current = '';
        setHiddenProfile(initialHiddenProfile);
        setPanStatusFeedback(null);
        setPanVerifyStatus(0);
        setPanResponseData(null);
        setPanLeadBlocked(false);
        setPanVerified(false);
        pendingAadhaarLocationRef.current = null;
        pendingPincodeRef.current = '';
        setStateId('');
        setCityId('');
        setCities([]);
        setPincodeList([]);
      }
    } else if (field === 'aadhaarNumber') {
      nextValue = formatAadhaarRaw(value);
    } else if (field === 'otp') {
      nextValue = value.replace(/\D/g, '').slice(0, 6);
    }

    setStep1((prev) => {
      const updated = { ...prev, [field]: nextValue };
      if (field === 'aadhaarNumber' && otpSent) {
        updated.otp = '';
      }
      return updated;
    });

    if (field === 'aadhaarNumber' && otpSent) {
      setOtpSent(false);
      setAadhaarOtpSentAt(0);
      setOtpVerified(false);
      setOtpApiMessage(null);
      setOtpCountdown(0);
    }

    if (field === 'otp') {
      setOtpApiMessage(null);
    }

    clearFieldError(field);

    if (field === 'panNumber' && nextValue.length === 10) {
      const pan = nextValue.toUpperCase();
      if (panCheckInFlightRef.current === pan || lastVerifiedPanRef.current === pan) {
        return;
      }
      checkPan(pan);
    }
  };

  const handleStep2Change = (e) => {
    const { id, value, type, checked } = e.target;
    let nextValue = value;

    if (id === 'mobile' || id === 'altMobile') {
      nextValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (id === 'monthlyIncome' || id === 'loanAmount' || id === 'obligations') {
      nextValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (id === 'email' || id === 'officialEmail') {
      nextValue = value.slice(0, MAX_EMAIL_LENGTH);
    } else if (id === 'address') {
      nextValue = value.slice(0, MAX_ADDRESS_LENGTH);
    }

    setStep2((prev) => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : nextValue,
    }));
    clearFieldError(id);
  };

  const validateStep1Field = (field) => {
    let message = '';

    if (field === 'panNumber') message = validatePan(step1.panNumber);
    else if (field === 'aadhaarNumber') message = validateAadhaar(step1.aadhaarNumber);
    else if (field === 'otp') message = validateOtp(step1.otp);

    if (message) setFieldError(field, message);
    else clearFieldError(field);
    return !message;
  };

  const validateStep2Field = (field) => {
    let message = '';

    switch (field) {
      case 'monthlyIncome':
        message = validateAmount(step2.monthlyIncome, {
          label: 'Monthly income',
          min: MIN_MONTHLY_INCOME,
          max: MAX_MONTHLY_INCOME,
        });
        break;
      case 'loanAmount':
        message = validateAmount(step2.loanAmount, {
          label: 'Loan amount',
          min: MIN_LOAN_AMOUNT,
          max: MAX_LOAN_AMOUNT,
        });
        if (!message && step2.monthlyIncome) {
          const income = Number(step2.monthlyIncome);
          const loan = Number(step2.loanAmount);
          if (ONLY_DIGITS.test(step2.monthlyIncome) && loan > income * 5) {
            message = 'Loan amount cannot exceed 5× your monthly income';
          }
        }
        break;
      case 'mobile':
        message = validateMobile(step2.mobile);
        break;
      case 'altMobile':
        message = validateMobile(step2.altMobile, { required: false, label: 'Alternate mobile' });
        if (!message && step2.altMobile && step2.altMobile === step2.mobile) {
          message = 'Alternate mobile must be different from mobile';
        }
        break;
      case 'email':
        message = validateEmail(step2.email);
        break;
      case 'officialEmail':
        message = validateEmail(step2.officialEmail, {
          required: false,
          label: 'Official email',
        });
        if (
          !message &&
          step2.officialEmail.trim() &&
          step2.email.trim() &&
          step2.officialEmail.trim().toLowerCase() === step2.email.trim().toLowerCase()
        ) {
          message = 'Official email must be different from personal email';
        }
        break;
      case 'obligations':
        if (step2.obligations) {
          message = validateAmount(step2.obligations, {
            required: false,
            label: 'Obligations',
            min: 0,
            max: MAX_OBLIGATIONS,
            allowZero: true,
          });
          if (
            !message &&
            step2.monthlyIncome &&
            ONLY_DIGITS.test(step2.monthlyIncome) &&
            Number(step2.obligations) > Number(step2.monthlyIncome)
          ) {
            message = 'Obligations cannot exceed monthly income';
          }
        }
        break;
      case 'state':
        if (!stateId || !step2.state) message = 'State is required';
        break;
      case 'city':
        if (!cityId || !step2.city) message = 'City is required';
        break;
      case 'pincode':
        message = validatePincode(step2.pincode);
        if (!message && cityId && pincodeList.length > 0) {
          const pinExists = pincodeList.some(
            (pin) => String(pin.m_pincode_value) === String(step2.pincode)
          );
          if (!pinExists) message = 'Please select a valid pincode';
        } else if (!message && cityId && pincodeList.length === 0) {
          message = 'No pincodes available for selected city';
        }
        break;
      case 'address':
        message = validateAddress(step2.address);
        break;
      case 'termsAccepted':
        if (!step2.termsAccepted) message = 'Please accept terms & conditions';
        break;
      default:
        break;
    }

    if (message) setFieldError(field, message);
    else clearFieldError(field);
    return !message;
  };

  const handleStep1Blur = (field) => {
    validateStep1Field(field);
    // PAN APIs only on 10th character (onChange) — blur pe dobara mat chalao
  };

  const handleStep2Blur = (field) => {
    validateStep2Field(field);
  };

  const validateIdentityFields = () => {
    const identityFields = ['panNumber', 'aadhaarNumber'];
    const nextErrors = {};

    const panError = validatePan(step1.panNumber);
    if (panError) nextErrors.panNumber = panError;

    const aadhaarError = validateAadhaar(step1.aadhaarNumber);
    if (aadhaarError) nextErrors.aadhaarNumber = aadhaarError;

    if (panLeadBlocked) {
      nextErrors.panNumber =
        errors.panNumber || panStatusFeedback?.message || 'Your loan is in progress. Cannot re-apply.';
    }

    setErrors((prev) => {
      const updated = { ...prev };
      identityFields.forEach((field) => delete updated[field]);
      return { ...updated, ...nextErrors };
    });
    return Object.keys(nextErrors).length === 0;
  };

  const blockPanLead = (pan, message, verifyPanSuccess = false) => {
    lastVerifiedPanRef.current = pan;
    setPanLeadBlocked(true);
    setPanStatusFeedback({ type: 'error', message });
    setFieldError('panNumber', message);
    return {
      blocked: true,
      checked: true,
      panVerifyStatus: verifyPanSuccess ? 1 : 0,
    };
  };

  const completePanCheck = (pan, feedback, verified = 0) => {
    const normalizedStatus = verified === 1 ? 1 : 0;
    lastVerifiedPanRef.current = pan;
    setPanLeadBlocked(false);
    setPanVerified(normalizedStatus === 1);
    setPanVerifyStatus(normalizedStatus);
    clearFieldError('panNumber');
    if (feedback) setPanStatusFeedback(feedback);
    return { blocked: false, checked: true, panVerifyStatus: normalizedStatus };
  };

  const verifyPanRequest = async (pan) => {
    const resp = await axios.post(
      loanBackendUrl('verifyPan.php'),
      { pan },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
    return resp.data;
  };

  const checkPan = async (panInput) => {
    const pan = String(panInput ?? step1.panNumber).trim().toUpperCase();
    const panError = validatePan(pan);

    if (pan.length !== 10) return { blocked: false, checked: false };

    if (panError) {
      setPanStatusFeedback(null);
      setFieldError('panNumber', panError);
      return { blocked: false, checked: false };
    }

    // Same PAN already checked (ref only — avoid stale state causing 2nd verifyPan)
    if (lastVerifiedPanRef.current === pan) {
      return {
        blocked: panLeadBlocked,
        checked: true,
        panVerifyStatus: panVerifyStatus === 1 ? 1 : 0,
      };
    }

    // Same PAN already in-flight
    if (panCheckInFlightRef.current === pan) {
      return { blocked: false, checked: false };
    }

    panCheckInFlightRef.current = pan;
    setPanChecking(true);
    setPanStatusFeedback(null);
    setPanVerifyStatus(0);
    setPanResponseData(null);
    setPanLeadBlocked(false);
    setPanVerified(false);
    clearFieldError('panNumber');

    try {

      // 1) Cross Vertical Check
// try {
//   const crossResp = await axios.get(crossVerticalPanUrl(pan), {
//       timeout: 30000,
//   });

//   const crossVerticalData = crossResp.data;

//   if (crossVerticalData?.status === false) {

//       const apiMsg = getCrossVerticalApiMessage(
//           crossVerticalData,
//           null,
//           "Unable to verify loan status."
//       );

//       const isNotFoundMsg =
//           String(apiMsg || "")
//               .toLowerCase()
//               .includes("pan not found in any database");

//       if (!isNotFoundMsg) {
//           return blockPanLead(
//               pan,
//               apiMsg,
//               0
//           );
//       }
//   }

//   if (isCrossVerticalDisbursedBlocked(crossVerticalData)) {
//       return blockPanLead(
//           pan,
//           CROSS_VERTICAL_DISBURSED_BLOCK_MESSAGE,
//           0
//       );
//   }

// } catch (crossErr) {

//   console.error(crossErr);

//   const apiMsg = getCrossVerticalApiMessage(
//       crossErr?.response?.data,
//       crossErr,
//       "Unable to verify loan status."
//   );

//   return blockPanLead(
//       pan,
//       apiMsg,
//       0
//   );

// }
      const [verifyPanResult, blacklistResp, customerResp] = await Promise.all([
        verifyPanRequest(pan).catch((err) => ({ failed: true, error: err })),
        axios
          .get(
            `${taskApiDirectUrl('checkGetCustomerbyPanBlacklist')}?pancard=${encodeURIComponent(pan)}`
          )
          .catch((err) => ({ failed: true, error: err })),
        axios
          .get(
            `${taskApiDirectUrl('checkGetCustomerbyPan')}?pancard=${encodeURIComponent(pan)}`
          )
          .catch((err) => ({ failed: true, error: err })),
      ]);

      if (blacklistResp?.failed) {
        console.warn('Blacklist check unavailable:', blacklistResp.error);
      }

      if (customerResp?.failed) {
        console.warn('Customer lookup unavailable:', customerResp.error);
      }

      const blacklistData = blacklistResp?.failed ? null : blacklistResp?.data;
      if (blacklistData?.blacklisted === true) {
        setPanVerifyStatus(0);
        setPanStatusFeedback({
          type: 'error',
          message: blacklistData.message || 'Customer is blacklisted.',
        });
        setFieldError('panNumber', blacklistData.message || 'Customer is blacklisted.');
        lastVerifiedPanRef.current = pan;
        return { blocked: true, checked: true };
      }

      if (isPanNotFoundResponse(verifyPanResult)) {
        return blockPanLead(pan, PAN_NOT_FOUND_MESSAGE, 0);
      }

      let verifyPanSuccess = false;

      if (isVerifyPanSuccess(verifyPanResult)) {
        const panData = verifyPanResult.data;
        setPanResponseData(verifyPanResult);
        setHiddenProfile((prev) => ({
          ...prev,
          firstName: panData.firstName || prev.firstName,
          middleName: prev.middleName,
          lastName: panData.lastName || prev.lastName,
          dob: panData.dateOfBirth || panData.dob || prev.dob,
          gender: panData.gender ? mapGender(panData.gender) : prev.gender,
          aadhaarLastFour:
            extractAadhaarLastFour(panData.maskedAadhaarNumber) || prev.aadhaarLastFour,
        }));

        if (panData.address) {
          setStep2((prev) => ({
            ...prev,
            address: prev.address || panData.address,
          }));
        }

        verifyPanSuccess = true;
      } else if (verifyPanResult?.status === false) {
        setPanStatusFeedback({
          type: 'warn',
          message:
            verifyPanResult.message ||
            'PAN could not be verified online. You may continue with Aadhaar verification.',
        });
      } else if (verifyPanResult?.failed) {
        console.warn('verifyPan API unavailable:', verifyPanResult.error);
        setPanStatusFeedback({
          type: 'info',
          message: 'PAN verification service is temporarily unavailable. You may continue.',
        });
      }

      const customerData = customerResp?.failed
        ? customerResp?.error?.response?.data || null
        : customerResp?.data;

      if (isPanNotFoundResponse(customerData)) {
        if (verifyPanSuccess) {
          return completePanCheck(
            pan,
            {
              type: 'info',
              message: 'No existing customer found. Continue with Aadhaar verification.',
            },
            1
          );
        }

        return blockPanLead(pan, PAN_NOT_FOUND_MESSAGE, 0);
      }

      if (customerData?.status === true && customerData?.data) {
        const profile = mapCustomerToHiddenProfile(customerData.data);
        const leadDecision = getLeadStatusDecision(customerData.data);

        setHiddenProfile((prev) => ({
          firstName: profile.firstName || prev.firstName,
          middleName: profile.middleName || prev.middleName,
          lastName: profile.lastName || prev.lastName,
          dob: profile.dob || prev.dob,
          gender: profile.gender || prev.gender,
        }));

        if (!leadDecision.allowed) {
          return blockPanLead(pan, leadDecision.feedback.message, verifyPanSuccess);
        }

        setPanStatusFeedback(leadDecision.feedback);
        return completePanCheck(pan, null, verifyPanSuccess ? 1 : 0);
      }

      if (isCustomerNotFoundResponse(customerData)) {
        if (verifyPanSuccess) {
          return completePanCheck(pan, {
            type: 'success',
            message: 'PAN verified successfully.',
          }, 1);
        }

        return completePanCheck(pan, {
          type: 'info',
          message: 'No existing customer found. Continue with Aadhaar verification.',
        }, 0);
      }

      if (isLoanBlockedResponse(customerData)) {
        return blockPanLead(
          pan,
          customerData?.message || 'Your loan status does not allow re-apply.',
          verifyPanSuccess
        );
      }

      if (verifyPanSuccess) {
        return completePanCheck(pan, {
          type: 'success',
          message: 'PAN verified successfully.',
        }, 1);
      }

      if (customerResp?.failed) {
        return completePanCheck(pan, {
          type: 'warn',
          message: 'Customer lookup is temporarily unavailable. You may continue with Aadhaar verification.',
        }, 0);
      }

      return completePanCheck(pan, {
        type: 'warn',
        message: customerData?.message || 'Unable to fetch full customer details. You may continue.',
      }, 0);
    } catch (err) {
      console.error('PAN check failed:', err);

      const status = err.response?.status;
      const responseData = err.response?.data;
      let message = '';

      if (typeof responseData === 'string' && responseData) {
        try {
          message = JSON.parse(responseData).message || responseData;
        } catch {
          message = responseData;
        }
      } else if (responseData?.message) {
        message = responseData.message;
      }

      if (status === 404 || isCustomerNotFoundResponse(responseData)) {
        return completePanCheck(pan, {
          type: 'info',
          message: 'No existing customer found. Continue with Aadhaar verification.',
        }, 0);
      }

      return completePanCheck(pan, {
        type: 'warn',
        message: message || 'Unable to verify PAN completely. You may continue.',
      }, 0);
    } finally {
      if (panCheckInFlightRef.current === pan) {
        panCheckInFlightRef.current = '';
      }
      setPanChecking(false);
    }
  };

  const ensurePanChecked = async () => {
    const pan = step1.panNumber.trim().toUpperCase();
    if (!pan || pan.length !== 10) return { blocked: false, checked: false, panVerifyStatus: 0 };

    if (lastVerifiedPanRef.current === pan) {
      return {
        blocked: panLeadBlocked,
        checked: true,
        panVerifyStatus: panVerifyStatus === 1 ? 1 : 0,
      };
    }

    return checkPan(pan);
  };

  const sendAadhaarOtpRequest = async (aadhaarNumber) => {
    const resp = await axios.post(
      loanBackendUrl('sendAadhaarOtp.php'),
      { aadhaar_number: aadhaarNumber },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
    return resp.data;
  };

  const verifyAadhaarOtpRequest = async (aadhaarNumber, otp) => {
    const resp = await axios.post(
      loanBackendUrl('verifyAadhaarOtp.php'),
      { aadhaar_number: aadhaarNumber, otp },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
    return resp.data;
  };

  const loadCitiesForState = async (sid) => {
    if (!sid) {
      setCities([]);
      setLoadingCities(false);
      return [];
    }

    const requestId = ++citiesRequestRef.current;
    setLoadingCities(true);

    try {
      const resp = await axios.post(
        taskApiPostUrl('getCity'),
        { state_id: String(sid) },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (requestId !== citiesRequestRef.current) return [];

      if (resp.data.Status === 1) {
        const cityData = Array.isArray(resp.data.Data) ? resp.data.Data : [];
        setCities(cityData);
        return cityData;
      }

      setCities([]);
      return [];
    } catch (err) {
      console.error('Failed fetching cities:', err);
      if (requestId === citiesRequestRef.current) {
        setCities([]);
      }
      return [];
    } finally {
      if (requestId === citiesRequestRef.current) {
        setLoadingCities(false);
      }
    }
  };

  const loadPincodesForCity = async (cid) => {
    if (!cid) {
      setPincodeList([]);
      setLoadingPincodes(false);
      return [];
    }

    const requestId = ++pincodesRequestRef.current;
    setLoadingPincodes(true);

    try {
      const resp = await axios.post(
        taskApiPostUrl('getPincodeByCityId'),
        { city_id: String(cid) },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (requestId !== pincodesRequestRef.current) return [];

      if (resp.data.Status === 1) {
        const pinData = Array.isArray(resp.data.Data) ? resp.data.Data : [];
        setPincodeList(pinData);
        return pinData;
      }

      setPincodeList([]);
      return [];
    } catch (err) {
      console.error('Failed fetching pincodes:', err);
      if (requestId === pincodesRequestRef.current) {
        setPincodeList([]);
      }
      return [];
    } finally {
      if (requestId === pincodesRequestRef.current) {
        setLoadingPincodes(false);
      }
    }
  };

  const handleStateChange = (sid) => {
    const selectedState = states.find((s) => String(s.state_id) === String(sid));

    pendingAadhaarLocationRef.current = null;
    pendingPincodeRef.current = '';
    setStep2((prev) => ({
      ...prev,
      state: selectedState?.state || '',
      city: '',
      pincode: '',
    }));
    setStateId(sid ? String(sid) : '');
    setCityId('');
    setPincodeList([]);
    clearFieldError('state');
    clearFieldError('city');
    clearFieldError('pincode');
  };

  const handleCityChange = (cid) => {
    const selectedCity = cities.find((c) => String(c.city_id) === String(cid));

    pendingPincodeRef.current = '';
    setStep2((prev) => ({
      ...prev,
      city: selectedCity?.city || '',
      pincode: '',
    }));
    setCityId(cid ? String(cid) : '');
    clearFieldError('city');
    clearFieldError('pincode');
  };

  const handlePincodeChange = (pincodeValue) => {
    setStep2((prev) => ({ ...prev, pincode: pincodeValue }));
    clearFieldError('pincode');
  };

  useEffect(() => {
    if (!stateId) {
      setCities([]);
      setLoadingCities(false);
      return;
    }

    loadCitiesForState(stateId);
  }, [stateId]);

  useEffect(() => {
    if (!cityId) {
      setPincodeList([]);
      setLoadingPincodes(false);
      return;
    }

    loadPincodesForCity(cityId);
  }, [cityId]);

  const handleMobileChange = (value) => {
    const nextValue = value.replace(/\D/g, '').slice(0, 10);
    setMobileNumber(nextValue);

    if (mobileVerified || mobileOtpSent) {
      setMobileVerified(false);
      setMobileOtpSent(false);
      setMobileOtpSentAt(0);
      setMobileOtp('');
      setMobileOtpMessage(null);
      setOtpCountdown(0);
      setStep2((prev) => ({ ...prev, mobile: '' }));
    }

    clearFieldError('mobileNumber');
    clearFieldError('mobileOtp');
  };

  const handleMobileOtpChange = (value) => {
    setMobileOtp(value.replace(/\D/g, '').slice(0, 6));
    setMobileOtpMessage(null);
    clearFieldError('mobileOtp');
  };

  const handleSendMobileOtp = async (isResend = false) => {
    const mobileError = validateMobile(mobileNumber);
    if (mobileError) {
      setFieldError('mobileNumber', mobileError);
      return;
    }

    if (!isResend && mobileOtpSent && otpCountdown > 0) {
      return;
    }

    setSendingMobileOtp(true);
    setMobileOtpMessage(null);
    clearFieldError('mobileNumber');
    clearFieldError('mobileOtp');

    try {
      const response = await fetch(mobileSendOtpUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ mobile: mobileNumber.trim() }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok && !isMobileOtpApiSuccess(data)) {
        const message = extractMobileOtpMessage(
          data,
          `Failed to send OTP (${response.status}). Please try again.`
        );
        setMobileOtpMessage({ type: 'error', message });
        setFieldError('mobileNumber', message);
        return;
      }

      if (isMobileOtpApiSuccess(data)) {
        const sentAt = Date.now();
        setMobileOtpSent(true);
        setMobileOtpSentAt(sentAt);
        setMobileOtp('');
        setOtpCountdown(OTP_RESEND_SECONDS);
        setMobileOtpMessage({
          type: 'success',
          message: extractMobileOtpMessage(data, 'OTP sent successfully.'),
        });
        return;
      }

      const message = extractMobileOtpMessage(data, 'Failed to send OTP. Please try again.');
      setMobileOtpMessage({ type: 'error', message });
      setFieldError('mobileNumber', message);
    } catch (err) {
      const message =
        err.message?.includes('Network Error') || err.name === 'TypeError'
          ? 'Network error while sending OTP. Please try again.'
          : err.message || 'Failed to send OTP. Please try again.';
      setMobileOtpMessage({ type: 'error', message });
      setFieldError('mobileNumber', message);
    } finally {
      setSendingMobileOtp(false);
    }
  };

  const handleVerifyMobileOtp = async () => {
    const mobileError = validateMobile(mobileNumber);
    if (mobileError) {
      setFieldError('mobileNumber', mobileError);
      return;
    }

    const otpError = validateOtp(mobileOtp);
    if (otpError) {
      setFieldError('mobileOtp', otpError);
      return;
    }

    setVerifyingMobileOtp(true);
    setMobileOtpMessage(null);

    try {
      const response = await fetch(mobileVerifyOtpUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          mobile: mobileNumber.trim(),
          otp: mobileOtp.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok && !isMobileOtpApiSuccess(data)) {
        const message = extractMobileOtpMessage(
          data,
          `OTP verification failed (${response.status}). Please try again.`
        );
        setMobileOtpMessage({ type: 'error', message });
        setFieldError('mobileOtp', message);
        return;
      }

      if (isMobileOtpApiSuccess(data)) {
        setMobileVerified(true);
        setMobileOtpSent(false);
        setMobileOtpSentAt(0);
        setMobileOtp('');
        setOtpCountdown(0);
        setStep2((prev) => ({ ...prev, mobile: mobileNumber.trim() }));
        setMobileOtpMessage({
          type: 'success',
          message: extractMobileOtpMessage(data, 'Mobile verified successfully.'),
        });
        setStep(2);
        clearFieldError('mobileOtp');
        return;
      }

      const message = extractMobileOtpMessage(data, 'Invalid OTP. Please try again.');
      setMobileOtpMessage({ type: 'error', message });
      setFieldError('mobileOtp', message);
    } catch (err) {
      const message =
        err.message?.includes('Network Error') || err.name === 'TypeError'
          ? 'Network error while verifying OTP. Please try again.'
          : err.message || 'OTP verification failed. Please try again.';
      setMobileOtpMessage({ type: 'error', message });
      setFieldError('mobileOtp', message);
    } finally {
      setVerifyingMobileOtp(false);
    }
  };

  const handleSendOtp = async (isResend = false) => {
    if (!mobileVerified) {
      setStep(1);
      return;
    }

    if (!validateIdentityFields()) return;

    if (panChecking) return;

    if (!isResend && otpSent && otpCountdown > 0) {
      return;
    }

    if (!isResend) {
      const panResult = await ensurePanChecked();
      if (!panResult.checked || panResult.blocked) return;
    }

    setSendingOtp(true);
    clearFieldError('aadhaarNumber');
    clearFieldError('otp');
    setOtpApiMessage(null);

    try {
      const resp = await sendAadhaarOtpRequest(step1.aadhaarNumber.trim());
      const feedback = getAadhaarApiFeedback(resp, 'Failed to send OTP. Please try again.');

      if (resp?.responseCode === AADHAAR_OTP_SEND_SUCCESS) {
        const sentAt = Date.now();
        setOtpSent(true);
        setAadhaarOtpSentAt(sentAt);
        setStep1((prev) => ({ ...prev, otp: '' }));
        setOtpCountdown(OTP_RESEND_SECONDS);
        setOtpApiMessage({ type: 'success', ...feedback });
        return;
      }

      setOtpApiMessage({ type: 'error', ...feedback });
      setFieldError('aadhaarNumber', feedback.message);
    } catch (err) {
      const data = err.response?.data || {};
      const feedback = getAadhaarApiFeedback(data, err.message || 'Failed to send OTP. Please try again.');
      setOtpApiMessage({ type: 'error', ...feedback });
      setFieldError('aadhaarNumber', feedback.message);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!mobileVerified) {
      setStep(1);
      return;
    }

    if (!validateIdentityFields()) return;

    if (panChecking) return;

    const panResult = await ensurePanChecked();
    if (!panResult.checked || panResult.blocked) return;

    const otpError = validateOtp(step1.otp);
    if (otpError) {
      setFieldError('otp', otpError);
      return;
    }

    setVerifyingOtp(true);
    setOtpApiMessage(null);

    try {
      const resp = await verifyAadhaarOtpRequest(
        step1.aadhaarNumber.trim(),
        step1.otp
      );
      const feedback = getAadhaarApiFeedback(resp, 'Invalid OTP. Please try again.');

      if (resp?.responseCode !== AADHAAR_OTP_VERIFY_SUCCESS || !resp?.data) {
        setOtpApiMessage({ type: 'error', ...feedback });
        setFieldError('otp', feedback.message);
        return;
      }

      setOtpApiMessage({ type: 'success', ...feedback });

      const aadhaarData = resp.data;
      let { firstName, middleName, lastName } = parseFullName(aadhaarData.name);
      let dob = aadhaarData.dateOfBirth || '';
      let gender = mapGender(aadhaarData.gender);

      firstName = firstName || hiddenProfile.firstName;
      middleName = middleName || hiddenProfile.middleName;
      lastName = lastName || hiddenProfile.lastName;
      dob = dob || hiddenProfile.dob;
      gender = gender || hiddenProfile.gender;

      const aadhaarLastFour =
        extractAadhaarLastFour(
          aadhaarData.maskedAadhaarNumber,
          aadhaarData.uid,
          aadhaarData.aadhaarLastFour,
          aadhaarData.lastFourDigits,
          step1.aadhaarNumber
        ) || hiddenProfile.aadhaarLastFour;

      setHiddenProfile({
        firstName,
        middleName,
        lastName,
        dob,
        gender,
        aadhaarLastFour,
      });

      const aadhaarAddress = buildAadhaarAddress(aadhaarData);
      pendingAadhaarLocationRef.current = null;
      pendingPincodeRef.current = '';
      setStateId('');
      setCityId('');
      setCities([]);
      setPincodeList([]);
      setStep2((prev) => ({
        ...prev,
        address: aadhaarAddress || prev.address,
        state: '',
        city: '',
        pincode: '',
        mobile:
          prev.mobile ||
          mobileNumber.trim() ||
          (aadhaarData.mobile && MOBILE_REGEX.test(String(aadhaarData.mobile))
            ? String(aadhaarData.mobile)
            : ''),
        email: aadhaarData.email || prev.email,
      }));

      setOtpVerified(true);
      setOtpSent(false);
      setAadhaarOtpSentAt(0);
      setOtpCountdown(0);
      setStep(3);
      clearFieldError('otp');
    } catch (err) {
      const data = err.response?.data || {};
      const feedback = getAadhaarApiFeedback(
        data,
        err.message || 'Unable to verify OTP. Please try again.'
      );
      setOtpApiMessage({ type: 'error', ...feedback });
      setFieldError('otp', feedback.message);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const validateStep2 = () => {
    const fields = [
      'monthlyIncome',
      'loanAmount',
      'mobile',
      'altMobile',
      'email',
      'officialEmail',
      'obligations',
      'state',
      'city',
      'pincode',
      'address',
      'termsAccepted',
    ];

    const nextErrors = {};

    fields.forEach((field) => {
      let message = '';

      switch (field) {
        case 'monthlyIncome':
          message = validateAmount(step2.monthlyIncome, {
            label: 'Monthly income',
            min: MIN_MONTHLY_INCOME,
            max: MAX_MONTHLY_INCOME,
          });
          break;
        case 'loanAmount':
          message = validateAmount(step2.loanAmount, {
            label: 'Loan amount',
            min: MIN_LOAN_AMOUNT,
            max: MAX_LOAN_AMOUNT,
          });
          if (!message && step2.monthlyIncome && ONLY_DIGITS.test(step2.monthlyIncome)) {
            const income = Number(step2.monthlyIncome);
            const loan = Number(step2.loanAmount);
            if (loan > income * 5) {
              message = 'Loan amount cannot exceed 5× your monthly income';
            }
          }
          break;
        case 'mobile':
          message = validateMobile(step2.mobile || mobileNumber);
          break;
        case 'altMobile':
          message = validateMobile(step2.altMobile, {
            required: false,
            label: 'Alternate mobile',
          });
          if (!message && step2.altMobile && step2.altMobile === step2.mobile) {
            message = 'Alternate mobile must be different from mobile';
          }
          break;
        case 'email':
          message = validateEmail(step2.email);
          break;
        case 'officialEmail':
          message = validateEmail(step2.officialEmail, {
            required: false,
            label: 'Official email',
          });
          if (
            !message &&
            step2.officialEmail.trim() &&
            step2.email.trim() &&
            step2.officialEmail.trim().toLowerCase() === step2.email.trim().toLowerCase()
          ) {
            message = 'Official email must be different from personal email';
          }
          break;
        case 'obligations':
          if (step2.obligations) {
            message = validateAmount(step2.obligations, {
              required: false,
              label: 'Obligations',
              min: 0,
              max: MAX_OBLIGATIONS,
              allowZero: true,
            });
            if (
              !message &&
              step2.monthlyIncome &&
              ONLY_DIGITS.test(step2.monthlyIncome) &&
              Number(step2.obligations) > Number(step2.monthlyIncome)
            ) {
              message = 'Obligations cannot exceed monthly income';
            }
          }
          break;
        case 'state':
          if (!stateId || !step2.state) message = 'State is required';
          break;
        case 'city':
          if (!cityId || !step2.city) message = 'City is required';
          break;
        case 'pincode':
          message = validatePincode(step2.pincode);
          if (!message && cityId && pincodeList.length > 0) {
            const pinExists = pincodeList.some(
              (pin) => String(pin.m_pincode_value) === String(step2.pincode)
            );
            if (!pinExists) message = 'Please select a valid pincode';
          } else if (!message && cityId && pincodeList.length === 0) {
            message = 'No pincodes available for selected city';
          }
          break;
        case 'address':
          message = validateAddress(step2.address);
          break;
        case 'termsAccepted':
          if (!step2.termsAccepted) message = 'Please accept terms & conditions';
          break;
        default:
          break;
      }

      if (message) nextErrors[field] = message;
    });

    setErrors((prev) => {
      const updated = { ...prev };
      fields.forEach((field) => delete updated[field]);
      return { ...updated, ...nextErrors };
    });
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Lock immediately so rapid multi-clicks cannot fire parallel submits
    if (submitInFlightRef.current || submitted) return;
    submitInFlightRef.current = true;
    setSubmitting(true);
    setSubmitError('');

    let didSucceed = false;

    try {
      if (!mobileVerified) {
        setSubmitError('Please verify your mobile number before submitting.');
        setStep(1);
        return;
      }

      if (!otpVerified) {
        setSubmitError('Please complete Aadhaar OTP verification before submitting.');
        setStep(2);
        return;
      }

      if (!validateStep2()) {
        setSubmitError('Please fix the highlighted fields before submitting.');
        return;
      }

      if (!stateId || !cityId) {
        if (!stateId) setFieldError('state', 'State is required');
        if (!cityId) setFieldError('city', 'City is required');
        setSubmitError('Please select state and city.');
        return;
      }

      if (!hiddenProfile.firstName || !hiddenProfile.dob || !hiddenProfile.gender) {
        setSubmitError('Identity details missing. Please verify Aadhaar OTP again.');
        setStep(2);
        return;
      }

      const pan = step1.panNumber.trim().toUpperCase();
      const panResult = await ensurePanChecked();
      if (!panResult.checked) {
        setSubmitError('Please enter and verify PAN before submitting.');
        setStep(2);
        return;
      }

      if (panResult.blocked || panLeadBlocked) {
        setSubmitError(
          panStatusFeedback?.message || 'Your loan is in progress. Cannot submit a new application.'
        );
        setStep(2);
        return;
      }

      // Use already-verified PAN data from Step 2 — do not re-hit verifyPan on submit
      const submitPanVerifyStatus = panResult.panVerifyStatus === 1 || panVerifyStatus === 1 ? 1 : 0;
      const submitPanResponseData = panResponseData;

      const payload = {
        loan_amount: String(step2.loanAmount).trim(),
        monthly_income: String(step2.monthlyIncome).trim(),
        first_name: hiddenProfile.firstName.trim(),
        middle_name: (hiddenProfile.middleName || '').trim(),
        sur_name: (hiddenProfile.lastName || '').trim(),
        pan,
        pan_status: submitPanVerifyStatus,
        ...buildPanPayloadFields(submitPanResponseData),
        dob: hiddenProfile.dob,
        gender: String(hiddenProfile.gender).toUpperCase(),
        mobile: (step2.mobile || mobileNumber).trim(),
        alternate_mobile: (step2.altMobile || '').trim(),
        email_personal: step2.email.trim(),
        email_official: (step2.officialEmail || '').trim(),
        obligations: step2.obligations ? Number(step2.obligations) : 0,
        state_id: String(stateId),
        city: String(cityId),
        pin: String(step2.pincode).trim(),
        address: step2.address.trim(),
        term_and_condition: step2.termsAccepted ? 'YES' : 'NO',
        ip: '106.219.215.60',
        coordinates: '',
        source: 'Loansalary',
        lead_data_source_id: 38,
        utm_source: '',
        utm_campaign: '',
        selfie_image_url: '',
        aadhaar_number: step1.aadhaarNumber.trim(),
        aadhaar_last_four:
          hiddenProfile.aadhaarLastFour || extractAadhaarLastFour(step1.aadhaarNumber),
      };

      const resp = await axios.post(saveLendingPageUrl(), payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      });

      if (resp.data?.Status === 1) {
        didSucceed = true;
        clearApplyNowDraft();
        savedDraftRef.current = null;
        setMobileOtp('');
        setStep1((prev) => ({ ...prev, otp: '' }));
        setSubmittedSummary({
          firstName: hiddenProfile.firstName.trim(),
          loanAmount: String(step2.loanAmount).trim(),
          mobile: (step2.mobile || mobileNumber).trim(),
          ref: `LS${Date.now().toString().slice(-8)}`,
        });
        setSubmitted(true);
        setSubmitError('');
        return;
      }

      const message = formatApiMessage(resp?.data?.Message) || 'Submit failed. Please try again.';
      setSubmitError(message);
    } catch (err) {
      let message = 'Submit failed. Please try again.';

      if (err.response) {
        message = formatApiMessage(err.response.data?.Message) ||
          `Server error (${err.response.status}). Please try again.`;
      } else if (err.request) {
        message = 'Network error. Please check your connection and try again.';
      } else if (err.message) {
        message = err.message;
      }

      setSubmitError(message);
    } finally {
      setSubmitting(false);
      // After success keep lock forever so another lead cannot be created
      if (!didSucceed) {
        submitInFlightRef.current = false;
      }
    }
  };

  const panValid = !validatePan(step1.panNumber);
  const aadhaarValid = !validateAadhaar(step1.aadhaarNumber);
  const otpValid = step1.otp.length === 6 && !validateOtp(step1.otp);
  const altMobileValid = !step2.altMobile || MOBILE_REGEX.test(step2.altMobile);
  const emailValid = !validateEmail(step2.email);

  const renderCustomDropdown = (props) => <CustomDropdown {...props} error={errors[props.id]} />;

  const renderTextareaField = (id, label, required, icon, value, onChange, options = {}) => (
    <div className={`apply-now-field apply-now-field-textarea ${errors[id] ? 'has-error' : ''}`}>
      <label htmlFor={id}>
        {label} {required && <span>*</span>}
      </label>
      <div className="apply-now-textarea-wrap">
        <span className="apply-now-textarea-icon">{icon}</span>
        <textarea
          id={id}
          value={value}
          onChange={onChange}
          onBlur={options.onBlur}
          placeholder={options.placeholder}
          maxLength={options.maxLength}
          rows={2}
        />
      </div>
      {errors[id] && !options.hideError && <span className="apply-now-error">{errors[id]}</span>}
    </div>
  );

  const renderInputField = (id, label, required, icon, value, onChange, options = {}) => (
    <div className={`apply-now-field ${errors[id] ? 'has-error' : ''}`}>
      <label htmlFor={id}>
        {label} {required && <span>*</span>}
      </label>
      <div className={`apply-now-input-wrap ${options.valid ? 'is-valid' : ''}`}>
        <span className="apply-now-input-icon">{icon}</span>
        <input
          id={id}
          value={value}
          onChange={onChange}
          onBlur={options.onBlur}
          placeholder={options.placeholder}
          maxLength={options.maxLength}
          inputMode={options.inputMode}
          disabled={options.disabled}
          autoComplete={options.autoComplete || 'off'}
          autoCorrect="off"
          spellCheck={false}
        />
        {options.valid && (
          <span className="apply-now-input-check">
            <FiCheckCircle />
          </span>
        )}
      </div>
      {errors[id] && !options.hideError && <span className="apply-now-error">{errors[id]}</span>}
    </div>
  );

  return (
    <div className="apply-now-page">
      <div className="apply-now-shell">
        <div className="apply-now-card">
          <aside className="apply-now-brand">
            <div className="apply-now-brand-content">
              <div className="apply-now-badge">
                <FiZap /> Quick Loan
              </div>
              <h2>Get funds in your account within minutes</h2>
              <p>
                Complete a quick identity check and unlock your personalised loan offer.
              </p>
              <ul className="apply-now-benefits">
                {BENEFITS.map(({ title, text }) => (
                  <li key={title}>
                    <span className="apply-now-benefit-check">
                      <FiCheckCircle />
                    </span>
                    <span>
                      <strong>{title}</strong>
                      <small>{text}</small>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="apply-now-brand-footer">
              <FiShield /> Trusted by <strong>50,000+</strong> happy customers
            </div>
          </aside>

          <section className="apply-now-form-panel">
            {submitted ? (
              <div className="apply-now-success">
                <div className="apply-now-success-confetti" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>

                <div className="apply-now-success-hero">
                  <div className="apply-now-success-icon-wrap">
                    <span className="apply-now-success-ring" />
                    <span className="apply-now-success-ring apply-now-success-ring--delay" />
                    <div className="apply-now-success-icon">
                      <FiCheckCircle />
                    </div>
                  </div>

                  <span className="apply-now-success-badge">Application Received</span>
                  <h3>
                    Thank You
                    {submittedSummary?.firstName ? `, ${submittedSummary.firstName}` : ''}!
                  </h3>
                  <p>
                    Your loan application has been submitted successfully. Keep your phone handy —
                    our team will reach out shortly with an update.
                  </p>
                </div>

                {submittedSummary && (
                  <div className="apply-now-success-summary">
                    <div className="apply-now-success-summary-item">
                      <small>Loan Amount</small>
                      <strong>₹{formatIndianCurrency(submittedSummary.loanAmount)}</strong>
                    </div>
                    <div className="apply-now-success-summary-item">
                      <small>Registered Mobile</small>
                      <strong>+91 {maskMobileNumber(submittedSummary.mobile)}</strong>
                    </div>
                    <div className="apply-now-success-summary-item">
                      <small>Reference No.</small>
                      <strong>{submittedSummary.ref}</strong>
                    </div>
                  </div>
                )}

                <div className="apply-now-success-steps">
                  <h4>What happens next?</h4>
                  <ol>
                    {SUCCESS_STEPS.map((item, index) => (
                      <li key={item.title}>
                        <span className="apply-now-success-step-num">{index + 1}</span>
                        <span>
                          <strong>{item.title}</strong>
                          <small>{item.text}</small>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="apply-now-success-actions">
                  <Link to="/" className="apply-now-btn apply-now-btn-primary">
                    <FiHome /> Back to Home
                  </Link>
                  <a href="tel:+919205959320" className="apply-now-btn apply-now-btn-outline">
                    <FiPhone /> Call Support
                  </a>
                </div>

                <div className="apply-now-success-support">
                  <FiMail />
                  <span>
                    Need help? Email us at{' '}
                    <a href="mailto:info@loanonsalary.com">info@loanonsalary.com</a>
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="apply-now-stepper">
                  <div
                    className={`apply-now-stepper-item ${step === 1 ? 'active' : ''} ${
                      mobileVerified ? 'done' : ''
                    }`}
                  >
                    <span className="apply-now-stepper-dot">1</span>
                    <span className="apply-now-stepper-text">
                      <small>Step 1</small>
                      Mobile
                    </span>
                  </div>
                  <div className="apply-now-stepper-line" />
                  <div
                    className={`apply-now-stepper-item ${step === 2 ? 'active' : ''} ${
                      otpVerified ? 'done' : ''
                    }`}
                  >
                    <span className="apply-now-stepper-dot">2</span>
                    <span className="apply-now-stepper-text">
                      <small>Step 2</small>
                      Identity
                    </span>
                  </div>
                  <div className="apply-now-stepper-line" />
                  <div className={`apply-now-stepper-item ${step === 3 ? 'active' : ''}`}>
                    <span className="apply-now-stepper-dot">3</span>
                    <span className="apply-now-stepper-text">
                      <small>Step 3</small>
                      Loan Details
                    </span>
                  </div>
                </div>

                {step === 1 ? (
                  <>
                    <div className="apply-now-form-header">
                      <h3>Verify your mobile</h3>
                      <p>Enter your mobile number and verify with OTP to continue</p>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (mobileOtpSent) handleVerifyMobileOtp();
                        else handleSendMobileOtp();
                      }}
                    >
                      <div className="apply-now-grid apply-now-grid-single">
                        {renderInputField(
                          'mobileNumber',
                          'Mobile Number',
                          true,
                          <FiPhone />,
                          mobileNumber,
                          (e) => handleMobileChange(e.target.value),
                          {
                            placeholder: '10-digit mobile number',
                            maxLength: 10,
                            inputMode: 'numeric',
                            valid: MOBILE_REGEX.test(mobileNumber),
                            disabled: sendingMobileOtp || verifyingMobileOtp,
                            hideError: true,
                          }
                        )}
                        {!mobileOtpSent && mobileOtpMessage && (
                          <div className={`apply-now-otp-message is-${mobileOtpMessage.type}`}>
                            <span>{mobileOtpMessage.message}</span>
                          </div>
                        )}
                        {!mobileOtpSent && errors.mobileNumber && !mobileOtpMessage && (
                          <span className="apply-now-otp-message is-error">
                            {errors.mobileNumber}
                          </span>
                        )}

                        {mobileOtpSent && (
                          <div className="full-width apply-now-otp-block">
                            {renderInputField(
                              'mobileOtp',
                              'Enter Mobile OTP',
                              true,
                              <FiLock />,
                              mobileOtp,
                              (e) => handleMobileOtpChange(e.target.value),
                              {
                                placeholder: '6-digit OTP',
                                maxLength: 6,
                                inputMode: 'numeric',
                                autoComplete: 'one-time-code',
                                valid: mobileOtp.length === 6 && !validateOtp(mobileOtp),
                                hideError: true,
                              }
                            )}

                            {mobileOtpMessage && (
                              <div
                                className={`apply-now-otp-message is-${mobileOtpMessage.type}`}
                              >
                                <span>{mobileOtpMessage.message}</span>
                              </div>
                            )}

                            {errors.mobileOtp && !mobileOtpMessage && (
                              <span className="apply-now-otp-message is-error">
                                {errors.mobileOtp}
                              </span>
                            )}

                            <div className="apply-now-otp-actions">
                              {otpCountdown > 0 ? (
                                <span className="apply-now-otp-timer">
                                  Resend OTP in {formatCountdown(otpCountdown)}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="apply-now-otp-resend"
                                  onClick={() => handleSendMobileOtp(true)}
                                  disabled={sendingMobileOtp || verifyingMobileOtp}
                                >
                                  {sendingMobileOtp ? 'Sending OTP...' : 'Resend OTP'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="apply-now-send-wrap">
                        <button
                          type="submit"
                          className="apply-now-btn apply-now-btn-black"
                          disabled={sendingMobileOtp || verifyingMobileOtp}
                        >
                          {sendingMobileOtp
                            ? 'Sending OTP...'
                            : verifyingMobileOtp
                              ? 'Verifying...'
                              : mobileOtpSent
                                ? 'Verify Mobile & Continue'
                                : 'Send OTP'}
                          {!sendingMobileOtp && !verifyingMobileOtp && <FiArrowRight />}
                        </button>
                      </div>
                    </form>
                  </>
                ) : step === 2 ? (
                  <>
                    <div className="apply-now-form-header">
                      <h3>Verify your identity</h3>
                      <p>Enter your PAN &amp; Aadhaar — takes less than 2 minutes</p>
                    </div>

                    {mobileVerified && (
                      <div className="apply-now-pan-status-banner is-success">
                        <span className="apply-now-pan-status-icon">
                          <FiPhone />
                        </span>
                        <div className="apply-now-pan-status-copy">
                          <strong>Mobile verified</strong>
                          <span>+91 {maskMobileNumber(mobileNumber || step2.mobile)}</span>
                        </div>
                      </div>
                    )}

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!mobileVerified) {
                          setStep(1);
                          return;
                        }
                        if (otpSent) handleVerifyOtp();
                        else handleSendOtp();
                      }}
                    >
                      <div className="apply-now-grid apply-now-grid-single">
                        {renderInputField(
                          'panNumber',
                          'PAN Number',
                          true,
                          <FiCreditCard />,
                          step1.panNumber,
                          (e) => handleStep1Change('panNumber', e.target.value),
                          {
                            placeholder: 'ABCDE1234F',
                            maxLength: 10,
                            valid: panValid && panVerified,
                            disabled: panChecking,
                            hideError: true,
                            onBlur: () => handleStep1Blur('panNumber'),
                          }
                        )}
                        {panChecking && (
                          <span className="apply-now-info apply-now-pan-message">Checking PAN...</span>
                        )}
                        {!panChecking && panStatusFeedback && (
                          <span
                            className={`apply-now-pan-message is-${panStatusFeedback.type}`}
                          >
                            {panStatusFeedback.message}
                          </span>
                        )}
                        {!panChecking && !panStatusFeedback && errors.panNumber && (
                          <span className="apply-now-pan-message is-error">
                            {errors.panNumber}
                          </span>
                        )}

                        {renderInputField(
                          'aadhaarNumber',
                          'Aadhaar Number',
                          true,
                          <MdFingerprint />,
                          formatAadhaarDisplay(step1.aadhaarNumber),
                          (e) => handleStep1Change('aadhaarNumber', e.target.value),
                          {
                            placeholder: 'XXXX XXXX XXXX',
                            maxLength: 14,
                            inputMode: 'numeric',
                            valid: aadhaarValid,
                            hideError: true,
                            onBlur: () => handleStep1Blur('aadhaarNumber'),
                          }
                        )}
                        {!otpSent && otpApiMessage && (
                          <div className={`apply-now-otp-message is-${otpApiMessage.type}`}>
                            <span>{otpApiMessage.message}</span>
                          </div>
                        )}
                        {!otpSent && errors.aadhaarNumber && !otpApiMessage && (
                          <span className="apply-now-otp-message is-error">
                            {errors.aadhaarNumber}
                          </span>
                        )}

                        {otpSent && (
                          <div className="full-width apply-now-otp-block">
                            {renderInputField(
                              'otp',
                              'Enter Aadhaar OTP',
                              true,
                              <FiLock />,
                              step1.otp,
                              (e) => handleStep1Change('otp', e.target.value),
                              {
                                placeholder: '6-digit OTP',
                                maxLength: 6,
                                inputMode: 'numeric',
                                autoComplete: 'one-time-code',
                                valid: otpValid,
                                hideError: true,
                                onBlur: () => handleStep1Blur('otp'),
                              }
                            )}

                            {otpApiMessage && (
                              <div
                                className={`apply-now-otp-message is-${otpApiMessage.type}`}
                              >
                                <span>{otpApiMessage.message}</span>
                              </div>
                            )}

                            {errors.otp && !otpApiMessage && (
                              <span className="apply-now-otp-message is-error">
                                {errors.otp}
                              </span>
                            )}

                            <div className="apply-now-otp-actions">
                              {otpCountdown > 0 ? (
                                <span className="apply-now-otp-timer">
                                  Resend OTP in {formatCountdown(otpCountdown)}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="apply-now-otp-resend"
                                  onClick={() => handleSendOtp(true)}
                                  disabled={sendingOtp || verifyingOtp}
                                >
                                  {sendingOtp ? 'Sending OTP...' : 'Resend OTP'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="apply-now-send-wrap">
                        <button
                          type="submit"
                          className="apply-now-btn apply-now-btn-black"
                          disabled={
                            sendingOtp ||
                            verifyingOtp ||
                            panChecking ||
                            panLeadBlocked ||
                            !mobileVerified
                          }
                        >
                          {sendingOtp
                            ? 'Sending OTP...'
                            : verifyingOtp
                              ? 'Verifying...'
                              : otpSent
                                ? 'Verify OTP & Continue'
                                : 'Send OTP'}
                          {!sendingOtp && !verifyingOtp && <FiArrowRight />}
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <>
                    <div className="apply-now-form-header">
                      <h3>Loan details</h3>
                      <p>Share your income and contact details to complete the application.</p>
                    </div>

                    {panStatusFeedback && (
                      <div className={`apply-now-pan-status-banner is-${panStatusFeedback.type}`}>
                        <span className="apply-now-pan-status-icon">
                          <FiCreditCard />
                        </span>
                        <div className="apply-now-pan-status-copy">
                          <strong>PAN {step1.panNumber}</strong>
                          <span>{panStatusFeedback.message}</span>
                        </div>
                      </div>
                    )}

                    <form
                      onSubmit={handleSubmit}
                      className={submitting ? 'is-submitting' : ''}
                    >
                      <div className="apply-now-grid">
                        {renderInputField(
                          'monthlyIncome',
                          'Monthly Income',
                          true,
                          <LuIndianRupee />,
                          step2.monthlyIncome,
                          handleStep2Change,
                          {
                            placeholder: 'Min ₹15,000',
                            inputMode: 'numeric',
                            maxLength: 10,
                            valid: step2.monthlyIncome && !validateAmount(step2.monthlyIncome, {
                              label: 'Monthly income',
                              min: MIN_MONTHLY_INCOME,
                              max: MAX_MONTHLY_INCOME,
                            }),
                            onBlur: () => handleStep2Blur('monthlyIncome'),
                          }
                        )}

                        {renderInputField(
                          'loanAmount',
                          'Loan Amount',
                          true,
                          <LuIndianRupee />,
                          step2.loanAmount,
                          handleStep2Change,
                          {
                            placeholder: '₹10,000 – ₹5,00,000',
                            inputMode: 'numeric',
                            maxLength: 10,
                            valid: step2.loanAmount && !validateAmount(step2.loanAmount, {
                              label: 'Loan amount',
                              min: MIN_LOAN_AMOUNT,
                              max: MAX_LOAN_AMOUNT,
                            }),
                            onBlur: () => handleStep2Blur('loanAmount'),
                          }
                        )}

                        {renderInputField(
                          'altMobile',
                          'Alternate Mobile',
                          false,
                          <FiPhone />,
                          step2.altMobile,
                          handleStep2Change,
                          {
                            placeholder: 'Optional',
                            maxLength: 10,
                            inputMode: 'numeric',
                            valid: altMobileValid,
                            onBlur: () => handleStep2Blur('altMobile'),
                          }
                        )}

                        {renderInputField(
                          'email',
                          'Email',
                          true,
                          <FiFileText />,
                          step2.email,
                          handleStep2Change,
                          {
                            placeholder: 'name@email.com',
                            valid: emailValid,
                            onBlur: () => handleStep2Blur('email'),
                          }
                        )}

                        {renderInputField(
                          'officialEmail',
                          'Official Email',
                          false,
                          <FiFileText />,
                          step2.officialEmail,
                          handleStep2Change,
                          {
                            placeholder: 'work@company.com',
                            valid: step2.officialEmail && !validateEmail(step2.officialEmail, {
                              required: false,
                              label: 'Official email',
                            }),
                            onBlur: () => handleStep2Blur('officialEmail'),
                          }
                        )}

                        {renderInputField(
                          'obligations',
                          'Obligations',
                          false,
                          <LuIndianRupee />,
                          step2.obligations,
                          handleStep2Change,
                          {
                            placeholder: 'Monthly EMIs / obligations',
                            inputMode: 'numeric',
                            maxLength: 10,
                            onBlur: () => handleStep2Blur('obligations'),
                          }
                        )}

                        {renderCustomDropdown({
                          id: 'state',
                          label: 'State',
                          required: true,
                          icon: <FiMapPin />,
                          value: stateId,
                          placeholder: 'Select state',
                          options: states.map((state) => ({
                            value: String(state.state_id),
                            label: state.state,
                          })),
                          onChange: handleStateChange,
                        })}
                        {statesLoadError && (
                          <span className="apply-now-error apply-now-states-error">
                            {statesLoadError}
                          </span>
                        )}

                        {renderCustomDropdown({
                          id: 'city',
                          label: 'City',
                          required: true,
                          icon: <FiMapPin />,
                          value: cityId,
                          placeholder: !stateId
                            ? 'Select state first'
                            : loadingCities
                              ? 'Loading cities...'
                              : cities.length > 0
                                ? 'Select city'
                                : 'No cities available',
                          disabled: !stateId || loadingCities,
                          options: cities.map((city) => ({
                            value: String(city.city_id),
                            label: city.city,
                          })),
                          onChange: handleCityChange,
                        })}

                        {renderCustomDropdown({
                          id: 'pincode',
                          label: 'Pincode',
                          required: true,
                          icon: <FiMapPin />,
                          value: step2.pincode,
                          placeholder: !cityId
                            ? 'Select city first'
                            : loadingPincodes
                              ? 'Loading pincodes...'
                              : pincodeList.length > 0
                                ? 'Select pincode'
                                : 'No pincodes available',
                          disabled: !cityId || loadingPincodes || pincodeList.length === 0,
                          searchable: pincodeList.length > 6,
                          options: pincodeList.map((pin) => ({
                            value: String(pin.m_pincode_value),
                            label: String(pin.m_pincode_value),
                          })),
                          onChange: handlePincodeChange,
                        })}

                        {renderTextareaField(
                          'address',
                          'Address',
                          true,
                          <FiMapPin />,
                          step2.address,
                          handleStep2Change,
                          {
                            placeholder: 'House no., street, area, landmark (min 15 chars)',
                            maxLength: MAX_ADDRESS_LENGTH,
                            onBlur: () => handleStep2Blur('address'),
                          }
                        )}

                        <div className="full-width">
                          <label className={`apply-now-terms ${errors.termsAccepted ? 'has-error' : ''}`}>
                            <input
                              id="termsAccepted"
                              type="checkbox"
                              checked={step2.termsAccepted}
                              onChange={handleStep2Change}
                            />
                            <span>
                              I agree to the{' '}
                              <a
                                href="/terms-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Terms &amp; Conditions
                              </a>{' '}
                              and authorize Loanonsalary to contact me regarding my loan application.
                            </span>
                          </label>
                          {errors.termsAccepted && (
                            <span className="apply-now-error">{errors.termsAccepted}</span>
                          )}
                        </div>
                      </div>

                      <div className="apply-now-actions">
                        {submitError && (
                          <div className="apply-now-submit-error">{submitError}</div>
                        )}
                        <button
                          type="submit"
                          className="apply-now-btn apply-now-btn-primary"
                          disabled={submitting || panLeadBlocked || submitted}
                          aria-busy={submitting}
                        >
                          {submitting ? (
                            <>
                              <span className="apply-now-btn-spinner" aria-hidden="true" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              Submit Application
                              <FiArrowRight />
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </>
                )}

                <div className="apply-now-trust-row">
                  <span><FiLock /> 256-bit encryption</span>
                  <span><FiFileText /> No paperwork</span>
                  <span><FiDollarSign /> Zero hidden charges</span>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ApplyNow;
